/**
 * @module NotificationTypes
 * @package @geenius/notifications/shared
 * @description Declares the shared notifications domain model used by every
 * package variant. These types define the publishable contract for inbox items,
 * preferences, and package-level configuration.
 */

/**
 * Ordered list of supported delivery channels.
 */
export const NOTIFICATION_CHANNELS: readonly ['in-app', 'email', 'push', 'sms'] = [
  'in-app',
  'email',
  'push',
  'sms',
]

/**
 * Ordered list of supported notification status values.
 */
export const NOTIFICATION_STATUSES: readonly ['unread', 'read', 'dismissed'] = [
  'unread',
  'read',
  'dismissed',
]

/**
 * Ordered list of supported notification types.
 */
export const NOTIFICATION_TYPES: readonly [
  'info',
  'success',
  'warning',
  'error',
  'mention',
  'invite',
  'payment',
  'system',
] = [
  'info',
  'success',
  'warning',
  'error',
  'mention',
  'invite',
  'payment',
  'system',
]

/**
 * Supported delivery channels for a notification record.
 */
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number]

/**
 * Read state tracked for inbox items.
 */
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number]

/**
 * Semantic notification categories exposed to UI filters and badges.
 */
export type NotificationType = (typeof NOTIFICATION_TYPES)[number]

/**
 * Realtime transport strategies supported by the package configuration.
 */
export type NotificationRealtimeTransport = 'convex' | 'polling'

/**
 * Arbitrary scalar metadata attached to a notification.
 */
export type NotificationMetadata = Record<string, string | number | boolean | null>

/**
 * Quiet-hour window used to suppress deliveries.
 *
 * @property start 24-hour start time in `HH:mm` format.
 * @property end 24-hour end time in `HH:mm` format.
 */
export interface NotificationQuietHours {
  start: string
  end: string
}

/**
 * Provider configuration for a delivery channel that relies on a third-party
 * notification service.
 *
 * @property provider Stable provider identifier such as `onesignal` or `resend`.
 * @property apiKey Optional credential or token reference for the provider.
 * @property endpoint Optional custom API or webhook endpoint.
 */
export interface NotificationProviderConfig {
  provider: string
  apiKey?: string
  endpoint?: string
}

/**
 * Shared notification record rendered by inbox, bell, and panel components.
 *
 * @property id Stable notification identifier.
 * @property type Semantic classification used for UI grouping and styling.
 * @property title Short headline presented in the inbox.
 * @property body Secondary copy that explains the event.
 * @property channel Delivery channel that carried the notification.
 * @property status Read-state value used for badges and filters.
 * @property userId Identifier for the receiving user.
 * @property metadata Optional structured metadata attached to the event.
 * @property createdAt ISO timestamp for when the item was created.
 * @property readAt Optional ISO timestamp for when the item was read.
 */
export interface Notification {
  id: string
  type: NotificationType
  title: string
  body: string
  channel: NotificationChannel
  status: NotificationStatus
  userId: string
  actorId?: string
  actorName?: string
  actorAvatar?: string
  resourceType?: string
  resourceId?: string
  resourceUrl?: string
  actionLabel?: string
  actionUrl?: string
  metadata?: NotificationMetadata
  createdAt: string
  readAt?: string
}

/**
 * Channel and type-level delivery preferences for a single user.
 *
 * @property userId Identifier for the user whose preferences are stored.
 * @property channels Per-channel enablement flags.
 * @property types Per-notification-type enablement flags.
 * @property quietHours Optional delivery suppression window.
 * @property timezone Optional IANA timezone used to interpret quiet hours.
 */
export interface NotificationPreferences {
  userId: string
  channels: Record<NotificationChannel, boolean>
  types: Record<NotificationType, boolean>
  quietHours?: NotificationQuietHours
  timezone?: string
}

/**
 * Paged notification response returned by inbox queries.
 *
 * @property notifications Page of notifications.
 * @property unreadCount Aggregate unread count for the current user.
 * @property hasMore Indicates whether more results are available.
 */
export interface NotificationBatch {
  notifications: Notification[]
  unreadCount: number
  hasMore: boolean
}

/**
 * Resolved package configuration used to initialize notification behavior.
 *
 * @property enabledChannels Channels allowed by default for deliveries.
 * @property defaultChannel Default channel used when no explicit channel is supplied.
 * @property realtime Optional realtime transport settings for inbox refreshes.
 * @property defaultPreferences Optional default user preference template.
 * @property providers Optional third-party provider mapping for delivery channels.
 */
export interface NotificationConfig {
  enabledChannels: NotificationChannel[]
  defaultChannel: NotificationChannel
  realtime?: {
    enabled: boolean
    transport: NotificationRealtimeTransport
    pollIntervalMs?: number
  }
  defaultPreferences?: Omit<NotificationPreferences, 'userId'>
  providers?: Partial<
    Record<Exclude<NotificationChannel, 'in-app'>, NotificationProviderConfig>
  >
}
